import React, { useEffect, useState } from 'react';
import { Badge, Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { addMatchRoster, listMatchRoster } from '../../../../lib/api/match';
import { listStaffByClub } from '../../../../lib/api/license';

const getClubImageUrl = imagePath =>
  imagePath
    ? `${process.env.REACT_APP_BASE_API_URL}/uploads/${imagePath}`
    : null;

const getStaffRowId = item =>
  item?.entities_id ??
  item?.staff_entities_id ??
  item?.staff_guid ??
  item?.clubs_staff_guid;

const toStaffOption = item => ({
  value: getStaffRowId(item),
  label: `${item.ent_name || ''} - (licenca ${item.license_name || ''}) - ${item.type_name || ''}`.trim(),
  raw: item
});

const rosterSelectStyles = {
  placeholder: base => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  }),
  singleValue: base => ({
    ...base,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 'calc(100% - 8px)'
  }),
  valueContainer: base => ({
    ...base,
    overflow: 'hidden'
  })
};

const canSetHeadCoach = item =>
  Number(item?.clubs_staff_type_id) === 1 || Number(item?.clubs_staff_type_id) === 2;

const mergeStaffRow = (row, staffRows) => {
  const matchedStaffRow = staffRows.find(
    item => String(item?.entities_id) === String(row?.entities_id)
  );

  return {
    ...matchedStaffRow,
    ...row
  };
};

const StaffRosterModal = ({ show, action, onClose, onSaved }) => {
  const [rosterLoading, setRosterLoading] = useState(false);
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaffOption, setSelectedStaffOption] = useState(null);
  const [rosterEntries, setRosterEntries] = useState([]);
  const [saveRosterLoading, setSaveRosterLoading] = useState(false);

  useEffect(() => {
    if (!show || !action || action.role !== 'staff') {
      return;
    }

    const loadData = async () => {
      setRosterEntries([]);
      setSelectedStaffOption(null);
      setRosterLoading(true);

      try {
        const [staffResponse, rosterResponse] = await Promise.all([
          listStaffByClub(action.clubGuid),
          listMatchRoster({
            matches_guid: action.matchesGuid,
            role: action.role,
            club_guid: action.clubGuid
          })
        ]);

        const staffRows = Array.isArray(staffResponse)
          ? staffResponse
          : staffResponse?.items || [];
        const rosterRows = Array.isArray(rosterResponse)
          ? rosterResponse
          : rosterResponse?.items || [];

        setStaffOptions(staffRows.map(toStaffOption));
        setRosterEntries(
          rosterRows
            .filter(
              row =>
                Number(row.club_entities_id) === Number(action.clubEntitiesId) &&
                row.team_side === action.teamSide &&
                row.role === action.role
            )
            .map(row => {
              const mergedRow = mergeStaffRow(row, staffRows);

              return {
                rowId: mergedRow.entities_id ?? mergedRow.match_roster_guid,
                ...mergedRow,
                selected_entities_id: mergedRow.entities_id,
                is_head_coach: Number(mergedRow.is_head_coach) === 1
              };
            })
        );
      } catch (error) {
        console.error('Greska prilikom ucitavanja staff rostera:', error);
        setStaffOptions([]);
        setRosterEntries([]);
      } finally {
        setRosterLoading(false);
      }
    };

    loadData();
  }, [show, action]);

  const availableStaffOptions = staffOptions.filter(
    option =>
      !rosterEntries.some(
        entry => String(entry.rowId) === String(option.value)
      )
  );

  const handleStaffSelect = option => {
    setSelectedStaffOption(option);

    if (!option?.raw) {
      return;
    }

    setRosterEntries(prev => {
      if (prev.some(entry => String(entry.rowId) === String(option.value))) {
        return prev;
      }

      return [
        ...prev,
        {
          rowId: option.value,
          ...option.raw,
          selected_entities_id: option.raw?.entities_id,
          is_head_coach: false
        }
      ];
    });

    setSelectedStaffOption(null);
  };

  const handleRosterEntryChange = (entitiesId, key, value) => {
    setRosterEntries(prev =>
      prev.map(entry =>
        String(entry.rowId) === String(entitiesId)
          ? { ...entry, [key]: value }
          : entry
      )
    );
  };

  const handleRemoveRosterEntry = entitiesId => {
    setRosterEntries(prev =>
      prev.filter(entry => String(entry.rowId) !== String(entitiesId))
    );
  };

  const handleClose = () => {
    setStaffOptions([]);
    setSelectedStaffOption(null);
    setRosterEntries([]);
    setRosterLoading(false);
    setSaveRosterLoading(false);
    onClose();
  };

  const handleSaveRoster = async () => {
    if (!action || rosterEntries.length === 0) {
      handleClose();
      return;
    }

    const items = rosterEntries.map(entry => ({
      match_id: Number(action.matchId),
      club_entities_id: Number(action.clubEntitiesId),
      team_side: action.teamSide,
      role: action.role,
      entities_id: entry.selected_entities_id,
      shirt_number: null,
      is_libero: 0,
      is_captain: 0,
      is_head_coach: entry.is_head_coach ? 1 : 0
    }));
    const entityIds = items.map(item => String(item.entities_id));

    if (new Set(entityIds).size !== items.length) {
      toast.error(
        'Isti clan osoblja je unesen vise puta (isti entities_id). Uklonite duplikat prije cuvanja.'
      );
      return;
    }

    const headCoachCount = rosterEntries.filter(
      entry => canSetHeadCoach(entry) && entry.is_head_coach
    ).length;
    if (headCoachCount !== 1) {
      toast.error('Mora biti oznacen tacno jedan glavni trener.');
      return;
    }

    setSaveRosterLoading(true);

    try {
      await addMatchRoster({
        replace_role: action.role,
        items
      });

      if (onSaved) {
        await onSaved(action.matchesGuid);
      }

      toast.success('Staff roster je uspjesno sacuvan.');
      handleClose();
    } catch (error) {
      console.error('Greska prilikom cuvanja staff rostera:', error);
      toast.error(
        error?.body?.message ||
          error?.message ||
          'Doslo je do greske prilikom cuvanja staff rostera.'
      );
    } finally {
      setSaveRosterLoading(false);
    }
  };

  return (
      <Modal show={show} onHide={handleClose} size="xl">
      <Modal.Header closeButton className="bg-200">
        <Modal.Title as="div" className="w-100 h5">
          <div className="d-flex align-items-center gap-2">
            {action?.clubImagePath && (
              <img
                src={getClubImageUrl(action.clubImagePath)}
                alt={action?.label || 'Club'}
                style={{
                  width: 28,
                  height: 28,
                  objectFit: 'cover',
                  borderRadius: '50%'
                }}
              />
            )}
            <span>{action?.label || ''}</span>
            <Badge
              className="ms-auto d-none d-md-inline-flex badge rounded-pill bg-secondary fs-10"
              style={{ position: 'relative', top: 1 }}
            >
              {`${rosterEntries.length} staff`}
            </Badge>
          </div>
          <div className="mt-2 d-md-none">
            <Badge
              className="badge rounded-pill bg-secondary fs-10"
              style={{ position: 'relative', top: 1 }}
            >
              {`${rosterEntries.length} staff`}
            </Badge>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="g-3">
          <Col xs={12}>
            <Form.Group>
              <Form.Label>Izaberite osoblje</Form.Label>
              <Select
                value={selectedStaffOption}
                onChange={handleStaffSelect}
                options={availableStaffOptions}
                isClearable
                isLoading={rosterLoading}
                classNamePrefix="react-select"
                styles={rosterSelectStyles}
                placeholder="Pretraga po nazivu, licenci ili vrsti"
              />
            </Form.Group>
          </Col>
          <Col xs={12}>
            {rosterLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <>
                <div className="d-none d-md-block">
                  <Table responsive>
                    <thead className="bg-200">
                      <tr>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>#</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Naziv</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Licenca</th>
                        <th className="text-uppercase small text-muted" style={{ paddingTop: 10, paddingBottom: 10 }}>Vrsta</th>
                        <th className="text-uppercase small text-muted text-center" style={{ paddingTop: 10, paddingBottom: 10 }}>Glavni trener</th>
                        <th className="text-uppercase small text-muted text-end" style={{ paddingTop: 10, paddingBottom: 10 }}>Akcija</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rosterEntries.map((entry, index) => (
                        <tr key={entry.rowId}>
                          <td>{index + 1}</td>
                          <td>{entry.ent_name}</td>
                          <td>{entry.license_name}</td>
                          <td>{entry.type_name}</td>
                          <td className="text-center" style={{ paddingTop: 6, paddingBottom: 6 }}>
                            {canSetHeadCoach(entry) ? (
                              <Form.Check
                                type="checkbox"
                                className="d-inline-block"
                                style={{ transform: 'scale(1.2)' }}
                                checked={Boolean(entry.is_head_coach)}
                                onChange={event =>
                                  handleRosterEntryChange(
                                    entry.rowId,
                                    'is_head_coach',
                                    event.target.checked
                                  )
                                }
                              />
                            ) : null}
                          </td>
                          <td className="text-end" style={{ paddingTop: 6, paddingBottom: 6 }}>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              className="mb-1 btn btn-outline-danger"
                              onClick={() => handleRemoveRosterEntry(entry.rowId)}
                            >
                              Ukloni
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>

                <div className="d-md-none">
                  <Row className="g-3">
                    {rosterEntries.map((entry, index) => (
                      <Col xs={12} key={entry.rowId}>
                        <div
                          className="border rounded-4 bg-white h-100 p-3 shadow-sm"
                          style={{ borderColor: '#dfe3ee' }}
                        >
                          <div className="d-flex align-items-start justify-content-between gap-3">
                            <div className="text-700 fs-10 fw-semibold">#{index + 1}</div>
                            <Button
                              variant="link"
                              size="sm"
                              className="p-0 text-danger text-decoration-none"
                              onClick={() => handleRemoveRosterEntry(entry.rowId)}
                            >
                              <FontAwesomeIcon icon="trash-alt" />
                            </Button>
                          </div>

                          <div className="mt-3">
                            <div className="fw-semibold text-dark fs-8">
                              {entry.ent_name || '-'}
                            </div>
                            <div className="text-700 fs-10 mt-1">
                              {entry.license_name || '-'}
                            </div>
                            <div className="text-600 fs-10 mt-1">
                              {entry.type_name || '-'}
                            </div>
                          </div>

                          {canSetHeadCoach(entry) && (
                            <Row className="g-3 mt-1">
                              <Col xs={12}>
                                <Form.Check
                                  type="checkbox"
                                  label="Glavni trener"
                                  checked={Boolean(entry.is_head_coach)}
                                  onChange={event =>
                                    handleRosterEntryChange(
                                      entry.rowId,
                                      'is_head_coach',
                                      event.target.checked
                                    )
                                  }
                                />
                              </Col>
                            </Row>
                          )}
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              </>
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Odustani
        </Button>
        <Button
          variant="primary"
          onClick={handleSaveRoster}
          disabled={saveRosterLoading}
        >
          {saveRosterLoading ? 'Cuva se...' : 'Sacuvaj'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StaffRosterModal;
